/**
 * Le fil de la séance du tuteur (pur) : les cartes vivantes, leur vie dans la mémoire, et ce que la colonne
 * de droite montre — un seul fil, filtré sur l'élève regardé (Wajih, 02/11 au soir, tech-derisk point 9).
 *
 *  - **Les cartes** (`tutorLiveCards`) : le port de `useTutorEventsQueue` (les signaux vivants, leur instant,
 *    l'escalade, la croix retenue) et de `getEffectiveCta` (`tutorFeedAction`), plus le résumé du champion
 *    qui attend d'être lu (l'ancienne `ConversationSummaryPopup`, F-98).
 *  - **Leur vie** (`tutorLiveFeedSync`) : la durée de vie d'une carte est son signal (point 12) ; quand il
 *    tombe, la carte sort du bac et laisse une trace qui dit pourquoi (« Alice a baissé la main »). Le rendu
 *    vit tant qu'il attend validation, l'activité choisie seul 10 min (`signalEnd`).
 *  - **La colonne** (`tutorLiveFeedView`) : chaque entrée a un tronc commun (`id`, `studentId` — absent pour
 *    tout le groupe —, `at`, `kind`, `origin`). Le filtre est un paramètre, jamais une seconde liste : avec
 *    lui, les entrées de l'élève regardé et celles de tout le groupe ; sans lui (la mosaïque, ou le filtre
 *    retiré), tout — le fil du groupe n'a pas d'élève (`session.student` absent). « Ailleurs », la page le
 *    déduit des comptes de chaque élève et des cartes (`tutorSessionElsewhere`).
 *    Les instants de la colonne sont ISO (`isoOf`) : elle les écrit « 17:32 » et les range, minuit compris.
 *  - **L'historique, un niveau au-dessus** (Wajih, 04/11) : la mémoire garde aussi le mode du son et l'activité que
 *    chaque élève a devant lui, à chaque changement (`tutorLiveTimelineChanges`, des entrées `mode` et `focus` qui ne
 *    s'affichent pas) ; le ruban de la séance s'en déduit (`tutorSessionRibbon`), et les traces se rangent sous
 *    l'activité qu'il avait devant lui à leur instant (`groupHistoryByActivity`). V1 : ce que ce navigateur a vu.
 */
import { tutorFeedAction, tutorFeedPriority } from "../../../ui/formatters/tutorFeedFormatter"
import { tutorSessionTimeline, tutorThreadPoint } from "../../../ui/formatters/tutorSessionFormatter"
import {
    type UITutorDecision,
    type UITutorDecisions,
    type UITutorFeedItem,
    type UITutorLiveActivity,
    type UITutorRibbonEventKind,
    type UITutorSession,
    type UITutorSessionEntry,
    type UITutorSessionRibbon,
    type UITutorQuizOutcome,
    type UITutorSessionRequest,
    type UITutorSessionSetting,
    type UITutorSuggestion,
} from "../../../ui/types/tutorLive"
import { clockOf, type TutorQuizLine } from "../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import {
    autoActivityVerb,
    ESCALATION_DELAY_MS,
    formatElapsed,
    HYDRATION_MAX_AGE_MS,
    isEscalated,
    OUT_OF_APP_ALERT_MS,
    OUT_OF_APP_DEBOUNCE_MS,
    SAM_ELIGIBLE,
    samSentKey,
    signalEnd,
    signalHash,
    SIGNAL_VERB,
    type TutorLiveSignal,
    type TutorLiveSignalType,
} from "./tutorLiveFeedRules"

/** Le signal d'une carte du fil : ceux de l'ancien panneau, et le résumé du champion. */
export type TutorLiveCardSignal = TutorLiveSignalType | "sam_summary"

/** Une carte vivante : sa carte de présentation, et ce que ses gestes lisent. */
export type TutorLiveCard = {
    /** L'empreinte (`eventHash`) : l'identité de la carte et de sa trace. */
    id: string
    signal: TutorLiveCardSignal
    studentId: string
    startedAt: number
    /** La clé de l'horloge (`signalKey`) ; absente pour un résumé. */
    key?: string
    activityId?: string
    /** La conversation d'un résumé du champion. */
    conversationId?: string
    item: UITutorFeedItem
    /** La trace de sa naissance. */
    createdText: string
}

/** Une entrée de la mémoire (`data.tutorLiveSession`), le tronc commun du point 9. */
export type TutorLiveFeedRecord = {
    id: string
    /**
     * `mode` et `focus` : le mode du son et l'activité devant l'élève, à chaque changement (le fil, les groupes) ;
     * `app` : il sort de l'appli (`out`, plus de 3 s) ou y revient (`in`), les hachures du fil.
     */
    kind: "signal" | "trace" | "tutor" | "mode" | "focus" | "app"
    studentId?: string
    at: number
    origin: "local" | "server"
    signal?: TutorLiveCardSignal
    text?: string
    /** Les réglages d'une demande de génération, que sa bulle dit d'abord (round 4). */
    settings?: UITutorSessionSetting[]
    /** Une demande de génération : sa pente, sa source, le message tapé (le fil les dit à part). */
    request?: UITutorSessionRequest
    /** `escalated` : une carte tombée sans décision après son escalade (« sans réponse ») — caduque, pas résolue seule. */
    state?: { kind: "open" } | { kind: "stale" | "decided"; at: number; text: string; escalated?: boolean }
    /** Le mode du son (`dialogue`, `focused`) d'une entrée `mode`, l'activité (son id, `""` : aucune) d'une `focus`. */
    value?: string
    /** L'activité d'un rendu : son point du fil se dit juste ou faux d'après sa correction. */
    activityId?: string
}

export type TutorLiveStudentName = { id: string; firstname: string; avatarUrl?: string }

/** Un résumé du champion qui attend d'être lu (`liveMode.completedConversations`, non lu). */
export type TutorLiveCompletedConversation = {
    studentId: string
    conversationId: string
    at: number
    title?: string
    summary: string
    needsHelp?: boolean
    /**
     * Le quiz de prérequis de l'élève (`trigger_context: prerequisite_quiz`) : les données le rangent comme une
     * conversation du champion, le tuteur ne le voit jamais comme telle. Ni carte ni trace ici : sa ligne de quiz
     * vient du serveur (`quizLines`), réussi ou non.
     */
    quiz?: boolean
}

export type TutorLiveCardsInput = {
    signals: TutorLiveSignal[]
    startedAt: Record<string, number>
    samSentAtByKey: Record<string, number | undefined> | null | undefined
    /** Les empreintes écartées par la croix (`dismissedEventHashes`, persistées). */
    dismissed: string[]
    completed: TutorLiveCompletedConversation[]
    students: TutorLiveStudentName[]
    /** Le statut d'une activité rendue, lu dans les listes de l'élève (`activityReviewStatus`). */
    reviewStatus: (studentId: string, activityId: string | undefined) => string | null
    dialogueWith: string | null
    isWeb: boolean
    helperName: string
    now: number
}

const SUMMARY_PROOF_MAX = 170

function nameOf(students: TutorLiveStudentName[], id: string): TutorLiveStudentName {
    return students.find((student) => student.id === id) ?? { id, firstname: "L'élève" }
}

function verbOf(signal: TutorLiveSignal, helperName: string): string {
    if (signal.type === "sam_ongoing") return `discute avec ${helperName}`
    if (signal.type === "auto_activity") return autoActivityVerb(signal.evaluation)
    return SIGNAL_VERB[signal.type]
}

function validWhileOf(signal: TutorLiveCardSignal, firstname: string): string | undefined {
    switch (signal) {
        case "hand_raised":
            return `tant que ${firstname} a la main levée`
        case "exercise_done":
            return "tant que le rendu attend ta validation"
        case "auto_activity":
            return "pendant 10 min"
        default:
            return undefined
    }
}

/**
 * Ce qui ne demande rien au masteur (weekly du 03/11, W11 : « À décider ne garde que ce qui demande une
 * action ») : l'élève discute avec le champion, l'élève a choisi seul une activité. Une trace du fil, écrite
 * une fois (`tutorLiveFeedItems`), jamais une carte du bac. Un résumé du champion l'est aussi, sauf quand
 * il demande le masteur (`needsHelp`).
 */
const INFO_SIGNALS: ReadonlySet<TutorLiveCardSignal> = new Set<TutorLiveCardSignal>(["sam_ongoing", "auto_activity"])

/**
 * Une trace de fait, née une fois, à l'id de la carte qu'elle aurait eue (elle ne s'écrit qu'une fois). Son signal
 * et son activité donnent son point au fil.
 */
function traceOf(
    id: string,
    studentId: string,
    at: number,
    text: string,
    from?: { signal: TutorLiveCardSignal; activityId?: string }
): TutorLiveFeedRecord {
    return {
        id,
        kind: "trace",
        studentId,
        at,
        origin: "local",
        text,
        ...(from != null && { signal: from.signal }),
        ...(from?.activityId != null && { activityId: from.activityId }),
    }
}

/**
 * Les faits du fil, triés une fois (weekly du 03/11, W11) : une **carte** pour ce qui demande une action au
 * masteur, une **trace** pour le reste (l'élève avec le champion, une activité choisie seul, un résumé qui ne
 * demande pas le tuteur). Un quiz de prérequis n'est ni l'un ni l'autre : sa ligne vient du serveur (`quizLines`). Les cartes vivantes par priorité puis la plus ancienne d'abord
 * (`sortEventsByPriority`).
 */
export function tutorLiveFeedItems(input: TutorLiveCardsInput): {
    cards: TutorLiveCard[]
    traces: TutorLiveFeedRecord[]
} {
    const dismissed = new Set(input.dismissed)
    const cards: TutorLiveCard[] = []
    const traces: TutorLiveFeedRecord[] = []
    for (const signal of input.signals) {
        const startedAt = input.startedAt[signal.key] ?? input.now
        const id = signalHash(signal.type, signal.studentId, startedAt)
        if (dismissed.has(id)) continue
        const student = nameOf(input.students, signal.studentId)
        const verb = verbOf(signal, input.helperName)
        if (INFO_SIGNALS.has(signal.type)) {
            traces.push(traceOf(id, signal.studentId, startedAt, `${student.firstname} ${verb}`))
            continue
        }
        const end = signalEnd(signal, startedAt, input.now, input.reviewStatus(signal.studentId, signal.activityId))
        if (end != null) {
            // Un rendu corrigé tout seul (auto-correction) ne demande rien : pas de carte, mais son point juste ou
            // faux sur le fil (une trace, écrite une fois ; une carte déjà née garde son entrée).
            if (end === "reviewed")
                traces.push(
                    traceOf(id, signal.studentId, startedAt, `${student.firstname} ${verb}`, {
                        signal: signal.type,
                        activityId: signal.activityId,
                    })
                )
            continue
        }
        const samSentAt = input.samSentAtByKey?.[samSentKey(signal.type, signal.studentId)] ?? null
        const escalated = isEscalated(signal.type, startedAt, samSentAt, input.now)
        const action = tutorFeedAction({
            signal: signal.type,
            studentId: signal.studentId,
            firstname: student.firstname,
            helperName: input.helperName,
            samSent: samSentAt != null,
            escalated,
            dialogueWithStudentId: input.dialogueWith,
            isWeb: input.isWeb,
        })
        cards.push({
            id,
            signal: signal.type,
            studentId: signal.studentId,
            startedAt,
            key: signal.key,
            activityId: signal.activityId,
            createdText: `${student.firstname} ${verb}`,
            item: {
                id,
                signal: signal.type,
                priority: tutorFeedPriority(signal.type),
                student,
                text: verb,
                elapsed: formatElapsed(startedAt, input.now),
                action,
                escalated: escalated || undefined,
                sam: samSentAt != null && !escalated ? "acts" : undefined,
                // La croix : sur une carte qui demande quelque chose (`EventCard`, ni passive ni résolue).
                dismissible: action != null && action.passive !== true,
                validWhile: validWhileOf(signal.type, student.firstname),
            },
        })
    }
    for (const done of input.completed) {
        const id = `summary-${done.conversationId}`
        if (dismissed.has(id) || done.quiz === true) continue
        const student = nameOf(input.students, done.studentId)
        const title = done.title ?? "Résumé de conversation"
        const summaryText = `${input.helperName} a fini avec ${student.firstname} : ${title}`
        if (done.needsHelp !== true) {
            traces.push(traceOf(id, done.studentId, done.at, summaryText, { signal: "sam_summary" }))
            continue
        }
        const action = tutorFeedAction({
            signal: "sam_summary",
            studentId: done.studentId,
            firstname: student.firstname,
            helperName: input.helperName,
            isWeb: input.isWeb,
        })
        const proof =
            done.summary.length > SUMMARY_PROOF_MAX ? `${done.summary.slice(0, SUMMARY_PROOF_MAX)}…` : done.summary
        cards.push({
            id,
            signal: "sam_summary",
            studentId: done.studentId,
            startedAt: done.at,
            conversationId: done.conversationId,
            createdText: summaryText,
            item: {
                id,
                signal: "sam_summary",
                priority: tutorFeedPriority("sam_summary", { needsHelp: done.needsHelp }),
                student,
                text: title,
                elapsed: formatElapsed(done.at, input.now),
                proof,
                action,
                // Fermer la popup du résumé, c'était l'avoir lu (`markConversationAsRead`).
                dismissible: true,
            },
        })
    }
    const rank = { urgent: 1, normal: 3, low: 5 } as const
    cards.sort((a, b) => rank[a.item.priority] - rank[b.item.priority] || a.startedAt - b.startedAt)
    return { cards, traces }
}

const QUIZ_VERB: Record<UITutorQuizOutcome, string> = {
    passed: "a réussi son quiz de prérequis",
    failed: "n'a pas réussi son quiz de prérequis",
    interrupted: "a interrompu son quiz de prérequis",
}

/** Le quiz de prérequis fini d'un élève, dans l'historique : sa ligne de quiz, jamais une ligne du champion. */
export function quizEntryOf(line: Omit<TutorQuizLine, "studentId">, firstname: string): UITutorSessionEntry {
    return {
        kind: "quiz",
        id: line.id,
        at: isoOf(line.at),
        text: `${firstname} ${QUIZ_VERB[line.outcome]}`,
        outcome: line.outcome,
        score: line.score,
    }
}

/** Les cartes vivantes seules (`tutorLiveFeedItems`). */
export function tutorLiveCards(input: TutorLiveCardsInput): TutorLiveCard[] {
    return tutorLiveFeedItems(input).cards
}

/** La trace d'une carte dont le signal est tombé : pourquoi elle sort du bac. */
export function endText(signal: TutorLiveCardSignal, firstname: string, helperName: string): string {
    switch (signal) {
        case "hand_raised":
            return `${firstname} a baissé la main`
        case "out_of_app":
            return `${firstname} est revenu dans l'appli`
        case "silence":
            return `${firstname} a repris l'échange`
        case "sam_ongoing":
            return `${firstname} a fini avec ${helperName}`
        case "exercise_done":
            return `Le rendu de ${firstname} est corrigé`
        case "auto_activity":
            return `Le choix de ${firstname} date de plus de 10 min`
        case "sam_summary":
            return `Résumé de ${firstname} lu`
    }
}

/**
 * Ce qui change dans la mémoire : les cartes nées (à écrire) et celles dont le signal est tombé (à périmer,
 * avec leur trace). Rien quand rien ne change : l'écran n'écrit qu'au changement.
 */
export function tutorLiveFeedSync(
    records: TutorLiveFeedRecord[],
    cards: TutorLiveCard[],
    names: {
        students: TutorLiveStudentName[]
        helperName: string
        now: number
        /** L'envoi du champion par signal (`samSentAtByKey`) : l'escalade compte de lui. */
        samSentAtByKey?: Record<string, number>
    },
    traces: TutorLiveFeedRecord[] = []
): { opened: TutorLiveFeedRecord[]; closed: { id: string; at: number; text: string; escalated?: true }[] } {
    const live = new Set(cards.map((card) => card.id))
    const known = new Set(records.map((record) => record.id))
    const opened = cards
        .filter((card) => !known.has(card.id))
        .map((card): TutorLiveFeedRecord => ({
            id: card.id,
            kind: "signal",
            studentId: card.studentId,
            at: card.startedAt,
            origin: "local",
            signal: card.signal,
            text: card.createdText,
            state: { kind: "open" },
            ...(card.activityId != null && { activityId: card.activityId }),
        }))
    const closed = records
        .filter((record) => record.kind === "signal" && record.state?.kind === "open" && !live.has(record.id))
        .map((record) => {
            const signal = record.signal ?? "hand_raised"
            const escalated =
                signal !== "sam_summary" &&
                isEscalated(
                    signal,
                    record.at,
                    names.samSentAtByKey?.[samSentKey(signal, record.studentId ?? "")],
                    names.now
                )
            return {
                id: record.id,
                at: names.now,
                text: endText(signal, nameOf(names.students, record.studentId ?? "").firstname, names.helperName),
                ...(escalated && { escalated: true as const }),
            }
        })
    const traced = traces.filter((trace) => !known.has(trace.id))
    return { opened: traced.length > 0 ? [...opened, ...traced] : opened, closed }
}

/** Une entrée qui ne vit pas dans la mémoire : la ligne du champion (serveur), l'attente, une proposition. */
export type TutorLiveTransientEntry = { studentId?: string; at: number; entry: UITutorSessionEntry }

export type TutorLiveFeedViewInput = {
    cards: TutorLiveCard[]
    records: TutorLiveFeedRecord[]
    transient: TutorLiveTransientEntry[]
    students: TutorLiveStudentName[]
    /** L'élève regardé ; absent : la mosaïque. */
    selectedId?: string
    /** Les élèves avec le champion en ce moment (`ongoingCheckpointIds`). */
    withHelper: string[]
    /** Le dernier passage du tuteur sur chaque élève (ms). */
    seen: Record<string, number>
    /** La mémoire a commencé sur cet appareil après le début du live : la colonne le dit. */
    startedHereAt?: number
    /** Une carte décidée réapparaît après 3 s si son signal vit encore (`markResolved`). */
    now: number
    /** Les activités de chaque élève (la pellicule) : le fil filtré range ses traces sous elles. Absent : rien à ranger. */
    activitiesOf?: (studentId: string) => UITutorLiveActivity[]
}

export type TutorLiveFeedView = {
    /** Toutes les cartes ouvertes, de tout le groupe (la cloche, « Ailleurs »). */
    items: UITutorFeedItem[]
    /**
     * La séance de l'élève regardé, que l'onglet « Fil » du centre relit (et « Mes élèves » montre) : ses
     * lignes et celles de tout le groupe. En mosaïque, celle du groupe.
     */
    session: UITutorSession
    /** Ce qui attend une décision chez chaque élève (`countActiveEvents`) : sa vignette, la cloche. */
    pending: Record<string, number>
    /** « À traiter » : ce qui attend le masteur, de tous les élèves (`tutorDecisions`). */
    decisions: UITutorDecisions
}

/** Après un geste, la carte se tait 3 s (l'ancienne carte verte) ; son signal vivant la ramène ensuite. */
const DECIDED_QUIET_MS = 3000

/** L'instant d'une entrée de la colonne (ISO) : la colonne l'écrit à l'heure et range par lui, minuit compris. */
export function isoOf(ms: number): string {
    return new Date(ms).toISOString()
}

export function tutorLiveFeedView(input: TutorLiveFeedViewInput): TutorLiveFeedView {
    const recordsById = new Map(input.records.map((record) => [record.id, record]))
    const quiet = (card: TutorLiveCard) => {
        const state = recordsById.get(card.id)?.state
        return state?.kind === "decided" && input.now - state.at < DECIDED_QUIET_MS
    }
    const openCards = input.cards.filter((card) => !quiet(card))
    const items = openCards.map((card) => card.item)
    const cardsById = new Map(openCards.map((card) => [card.id, card]))

    const pending: Record<string, number> = {}
    for (const card of openCards) {
        if (card.item.action == null || card.item.action.passive === true) continue
        pending[card.studentId] = (pending[card.studentId] ?? 0) + 1
    }

    type Scoped<T> = { studentId?: string; value: T }
    const suggestions: Scoped<UITutorSuggestion>[] = []
    const entries: Scoped<UITutorSessionEntry>[] = []
    const seenIds = new Set<string>()
    for (const record of input.records) {
        if (record.kind === "signal") {
            seenIds.add(record.id)
            const card = cardsById.get(record.id)
            const at = isoOf(record.at)
            // Ouverte en mémoire sans carte ce rendu-ci : la synchronisation la périme au prochain effet.
            const state: UITutorSuggestion["state"] =
                card == null && record.state != null && record.state.kind !== "open"
                    ? { kind: record.state.kind, at: isoOf(record.state.at), text: record.state.text }
                    : { kind: "open" }
            suggestions.push({
                studentId: record.studentId,
                value: {
                    item: card?.item ?? placeholderItem(record, input.students),
                    at,
                    createdText: record.text ?? "",
                    state,
                },
            })
        } else if (record.kind === "tutor") {
            entries.push({
                studentId: record.studentId,
                value: {
                    kind: "tutor",
                    id: record.id,
                    at: isoOf(record.at),
                    text: record.text ?? "",
                    settings: record.settings,
                    request: record.request,
                },
            })
        } else if (record.kind === "trace") {
            entries.push({
                studentId: record.studentId,
                value: { kind: "trace", id: record.id, at: isoOf(record.at), text: record.text ?? "" },
            })
        }
    }
    // Une carte née ce rendu-ci, avant que la mémoire ne l'ait écrite.
    for (const card of openCards) {
        if (seenIds.has(card.id)) continue
        suggestions.push({
            studentId: card.studentId,
            value: {
                item: card.item,
                at: isoOf(card.startedAt),
                createdText: card.createdText,
                state: { kind: "open" },
            },
        })
    }
    for (const transient of input.transient) entries.push({ studentId: transient.studentId, value: transient.entry })

    const build = (scopeId: string | undefined): UITutorSession => {
        const inScope = (scoped: { studentId?: string }) =>
            scopeId == null || scoped.studentId == null || scoped.studentId === scopeId
        const { history, tray } = tutorSessionTimeline({
            entries: entries.filter(inScope).map((scoped) => scoped.value),
            suggestions: suggestions.filter(inScope).map((scoped) => scoped.value),
            lastSeenAt: scopeId != null && input.seen[scopeId] != null ? isoOf(input.seen[scopeId]) : undefined,
        })
        const student = scopeId != null ? nameOf(input.students, scopeId) : undefined
        return {
            student:
                student != null
                    ? { id: student.id, firstname: student.firstname, avatarUrl: student.avatarUrl }
                    : undefined,
            history:
                scopeId != null && input.activitiesOf != null
                    ? groupHistoryByActivity(
                          history,
                          focusSegments(input.records, scopeId, input.now),
                          input.activitiesOf(scopeId)
                      )
                    : history,
            tray,
            helperActive: scopeId != null && input.withHelper.includes(scopeId) ? true : undefined,
            startedHere: input.startedHereAt != null ? isoOf(input.startedHereAt) : undefined,
        }
    }

    return {
        decisions: tutorDecisions(openCards, input.transient, input.students),
        items,
        session: build(input.selectedId),
        pending,
    }
}

/** Les alertes : ce qui demande le masteur maintenant, en rangées compactes. */
const ALERT_RANK: Partial<Record<TutorLiveCardSignal, number>> = { hand_raised: 0, out_of_app: 1, silence: 2 }

/** Une entrée de génération attend une décision : en cours, prête et pas encore décidée, en échec, ou une proposition. */
function awaitsDecision(entry: UITutorSessionEntry): entry is Extract<UITutorDecision, { kind: "entry" }>["entry"] {
    switch (entry.kind) {
        case "progress":
            return true
        case "ready":
            return entry.decided !== true
        case "result":
            return entry.error != null
        case "proposal":
            return entry.state !== "sent"
        default:
            return false
    }
}

/**
 * « À traiter » (pur) : de tous les élèves, les alertes (main, sortie de l'appli, silence) d'abord — l'escaladée
 * devant, puis la main, la sortie, le silence, la plus ancienne d'abord —, puis les propositions, la plus ancienne
 * d'abord : un rendu, un résumé qui demande le masteur, une demande au champion en cours ou prête, une génération ratée,
 * une proposition du champion. Une décision prise la retire (la carte se tait, la demande est décidée, la proposition
 * envoyée).
 */
export function tutorDecisions(
    cards: TutorLiveCard[],
    transient: TutorLiveTransientEntry[],
    students: TutorLiveStudentName[]
): UITutorDecisions {
    const alertCards = cards.filter((card) => ALERT_RANK[card.signal] != null)
    alertCards.sort(
        (a, b) =>
            Number(b.item.escalated === true) - Number(a.item.escalated === true) ||
            (ALERT_RANK[a.signal] ?? 0) - (ALERT_RANK[b.signal] ?? 0) ||
            a.startedAt - b.startedAt
    )
    const timed: { at: number; decision: UITutorDecision }[] = cards
        .filter((card) => ALERT_RANK[card.signal] == null)
        .map((card) => ({
            at: card.startedAt,
            decision: { kind: "card", id: card.id, at: isoOf(card.startedAt), item: card.item },
        }))
    for (const one of transient) {
        const entry = one.entry
        if (!awaitsDecision(entry)) continue
        const student = one.studentId != null ? nameOf(students, one.studentId) : undefined
        timed.push({ at: one.at, decision: { kind: "entry", id: entry.id, at: isoOf(one.at), student, entry } })
    }
    timed.sort((a, b) => a.at - b.at)
    return { alerts: alertCards.map((card) => card.item), proposals: timed.map((one) => one.decision) }
}

/** La carte d'une entrée de la mémoire dont le signal n'est plus là : sans action, elle ne se touche plus. */
function placeholderItem(record: TutorLiveFeedRecord, students: TutorLiveStudentName[]): UITutorFeedItem {
    const student = nameOf(students, record.studentId ?? "")
    const signal = record.signal ?? "hand_raised"
    return {
        id: record.id,
        signal,
        priority: tutorFeedPriority(signal),
        student,
        text: record.text ?? "",
        elapsed: clockOf(record.at),
    }
}

// ── Le prochain réveil ───────────────────────────────────────────────────────────────────────────

export type TutorLiveFeedWakeInput = {
    /** Les cartes vivantes (`tutorLiveCards`), muettes comprises. */
    cards: TutorLiveCard[]
    records: TutorLiveFeedRecord[]
    /** Le premier instant vu hors de l'appli, par élève (l'anti-rebond). */
    outOfAppSince: Record<string, number>
    samSentAtByKey: Record<string, number | undefined> | null | undefined
    now: number
}

/** Le prochain changement du temps écoulé d'une carte (`formatElapsed`) : la seconde, puis la minute. */
function nextElapsedChange(startedAt: number, now: number): number {
    const age = Math.max(0, now - startedAt)
    const step = age < 60 * 1000 ? 1000 : 60 * 1000
    return startedAt + (Math.floor(age / step) + 1) * step
}

/**
 * L'instant où le fil change sans que rien d'autre ne bouge, ou `null` : rien n'attend. L'ancien panneau
 * relisait tout à la seconde (`tick`) ; ici l'écran ne se redessine qu'à ces échéances, une à la fois :
 *  - le temps écoulé d'une carte (`item.elapsed`, `TutorSessionFeedCard`) : à la seconde tant qu'il dit
 *    « 42s » (la première minute de la carte, le seul compte à la seconde du fil), puis à la minute ;
 *  - la fin de l'anti-rebond d'une sortie de l'appli (`OUT_OF_APP_DEBOUNCE_MS`) : ses hachures ; ses 3 min
 *    (`OUT_OF_APP_ALERT_MS`) : l'alerte naît ;
 *  - l'escalade d'une main ou d'un silence (`ESCALATION_DELAY_MS`, depuis l'envoi du champion s'il est parti) ;
 *  - la fin des 3 s de silence d'une carte décidée (`DECIDED_QUIET_MS`) : son signal vivant la ramène ;
 *  - l'instant d'un signal qui passe l'âge d'une autre séance (`HYDRATION_MAX_AGE_MS`), comme la prod.
 */
export function tutorLiveFeedNextWake(input: TutorLiveFeedWakeInput): number | null {
    const { now } = input
    let next = Infinity
    const consider = (at: number) => {
        if (at > now && at < next) next = at
    }
    for (const since of Object.values(input.outOfAppSince)) {
        consider(since + OUT_OF_APP_DEBOUNCE_MS)
        consider(since + OUT_OF_APP_ALERT_MS)
    }
    const live = new Set<string>()
    for (const card of input.cards) {
        live.add(card.id)
        consider(nextElapsedChange(card.startedAt, now))
        if (card.signal === "sam_summary") continue
        if (SAM_ELIGIBLE.has(card.signal)) {
            const sentAt = input.samSentAtByKey?.[samSentKey(card.signal, card.studentId)]
            consider((sentAt ?? card.startedAt) + ESCALATION_DELAY_MS)
        }
        consider(card.startedAt + HYDRATION_MAX_AGE_MS + 1)
    }
    for (const record of input.records) {
        if (record.state?.kind === "decided" && live.has(record.id)) consider(record.state.at + DECIDED_QUIET_MS)
    }
    return next === Infinity ? null : next
}

// ── L'historique, un niveau au-dessus : le ruban et les activités ───────────────────────────────────────

/** Ce que la mémoire sait d'un élève à un instant : son mode du son et l'activité qu'il a devant lui. */
/**
 * `away` : il n'est pas dans la salle — son mode par défaut ne dit rien de la séance. `outOfApp` : il est sorti de
 * l'appli depuis plus de 3 s (absent : on ne sait pas, rien ne s'écrit).
 */
export type TutorLiveStudentNow = {
    studentId: string
    mode: "dialogue" | "focused" | "away"
    focusId: string | null
    outOfApp?: boolean
}

type TimelineKind = "mode" | "focus" | "app"

/** La dernière valeur gardée d'un élève pour une sorte (`mode`, `focus`, `app`), ou `undefined` : jamais gardée. */
function lastValue(records: TutorLiveFeedRecord[], kind: TimelineKind, studentId: string): string | undefined {
    for (let i = records.length - 1; i >= 0; i--) {
        const record = records[i]
        if (record.kind === kind && record.studentId === studentId) return record.value ?? ""
    }
    return undefined
}

/**
 * Ce qu'il faut écrire dans la mémoire : un changement de mode ou d'activité devant l'élève depuis la dernière
 * valeur gardée. Rien quand rien ne change ; l'écran n'écrit qu'au changement.
 */
export function tutorLiveTimelineChanges(
    records: TutorLiveFeedRecord[],
    students: TutorLiveStudentNow[],
    now: number
): TutorLiveFeedRecord[] {
    const changes: TutorLiveFeedRecord[] = []
    for (const student of students) {
        if (lastValue(records, "mode", student.studentId) !== student.mode)
            changes.push({
                id: `mode-${student.studentId}-${now}`,
                kind: "mode",
                studentId: student.studentId,
                at: now,
                origin: "local",
                value: student.mode,
            })
        const focus = student.focusId ?? ""
        if (lastValue(records, "focus", student.studentId) !== focus)
            changes.push({
                id: `focus-${student.studentId}-${now}`,
                kind: "focus",
                studentId: student.studentId,
                at: now,
                origin: "local",
                value: focus,
            })
        if (student.outOfApp != null) {
            const app = student.outOfApp ? "out" : "in"
            const last = lastValue(records, "app", student.studentId)
            // Un retour ne s'écrit qu'après une sortie : un élève resté dans l'appli n'écrit rien.
            if (last !== app && (last != null || app === "out"))
                changes.push({
                    id: `app-${student.studentId}-${now}`,
                    kind: "app",
                    studentId: student.studentId,
                    at: now,
                    origin: "local",
                    value: app,
                })
        }
    }
    return changes
}

type Segment = { from: number; to: number; value: string }

/** Les plages d'une sorte pour un élève : chaque valeur gardée tient jusqu'à la suivante, la dernière jusqu'à maintenant. */
function segments(records: TutorLiveFeedRecord[], kind: TimelineKind, studentId: string, now: number): Segment[] {
    const mine = records
        .filter((record) => record.kind === kind && record.studentId === studentId)
        .sort((a, b) => a.at - b.at)
    return mine.map((record, index) => ({
        from: record.at,
        to: index + 1 < mine.length ? mine[index + 1].at : now,
        value: record.value ?? "",
    }))
}

const MINUTE = 60 * 1000
/** Un « rien devant lui » plus court que ceci est un rechargement ou une reconnexion, pas un moment de la séance. */
const FOCUS_BLIP = 2 * MINUTE

/**
 * Les plages d'activité d'un élève (sans les moments où il n'en avait aucune devant lui). Un trou bref entre deux
 * plages de la même activité — la salle rechargée, l'élève reconnecté, sa liste pas encore relue — ne coupe pas
 * l'activité en morceaux (vu sur la tablette : un ruban en miettes et deux groupes pour un même exercice).
 */
export function focusSegments(records: TutorLiveFeedRecord[], studentId: string, now: number): Segment[] {
    const all = segments(records, "focus", studentId, now)
    const kept: Segment[] = []
    for (const [index, segment] of all.entries()) {
        // Le dernier trou reste : il dit que l'élève n'a rien devant lui maintenant.
        const between = index < all.length - 1
        if (segment.value === "" && between && segment.to - segment.from < FOCUS_BLIP) continue
        const last = kept[kept.length - 1]
        if (last != null && last.value === segment.value) last.to = segment.to
        else kept.push({ ...segment })
    }
    return kept.filter((segment) => segment.value !== "")
}

/**
 * Le point du fil que porte une entrée de la mémoire, ou rien. Un rendu se dit juste ou faux d'après sa correction
 * (son titre et son score dans sa ligne) ; une main ou un silence tombés seuls s'effacent, restés sans réponse
 * jusqu'à l'escalade ils passent au gris ; une sortie de l'appli n'est pas un point, ce sont les hachures.
 */
function ribbonEvent(
    record: TutorLiveFeedRecord,
    activityOf: (id: string | undefined) => UITutorLiveActivity | undefined
): { kind: UITutorRibbonEventKind; label: string } | null {
    const text = record.text ?? ""
    if (record.kind === "tutor") return { kind: "sent", label: text }
    if (record.kind !== "signal" && record.kind !== "trace") return null
    if (record.signal === "exercise_done") {
        const activity = activityOf(record.activityId)
        const kind =
            activity?.reviewStatus === "validated" ? "ok" : activity?.reviewStatus === "rejected" ? "ko" : "done"
        const label =
            activity != null
                ? ["Rendu", activity.title, activity.score].filter((part) => part != null).join(" · ")
                : text
        return { kind, label }
    }
    if (record.signal === "sam_summary") return { kind: "helper", label: text }
    if (record.kind === "trace") return null
    if (record.signal !== "hand_raised" && record.signal !== "silence") return null
    const kind = record.signal === "hand_raised" ? "hand" : "silence"
    if (record.state?.kind !== "stale") return { kind, label: text }
    // Tombé sans décision : résolu seul (la main baissée, la parole revenue), il s'efface ; tombé après son escalade
    // (sans réponse), il est caduc — l'élève est passé à autre chose — et rejoint le fil en gris.
    return record.state.escalated === true ? { kind: "stale", label: text } : null
}

/** Un point qui ne vit pas dans la mémoire : un quiz de prérequis, une ligne du champion, une proposition envoyée (ms). */
export type TutorThreadExtra = { id: string; at: number; kind: UITutorRibbonEventKind; label: string }

/**
 * Les points du fil que la mémoire n'a pas, tirés des lignes du moment (`TutorLiveTransientEntry`), par élève : une
 * ligne du champion, un quiz de prérequis, une demande ajoutée ou une proposition envoyée (« donné »), une génération
 * ratée. Une ligne sans élève (pour tout le groupe) n'a pas de tuile où se poser.
 */
export function tutorThreadExtras(transient: TutorLiveTransientEntry[]): Record<string, TutorThreadExtra[]> {
    const out: Record<string, TutorThreadExtra[]> = {}
    for (const { studentId, at, entry } of transient) {
        if (studentId == null) continue
        const point = tutorThreadPoint(entry)
        if (point == null) continue
        ;(out[studentId] ??= []).push({ id: entry.id, at, ...point })
    }
    return out
}

/**
 * Deux plages du même mode qu'un trou bref sépare (la salle rechargée, l'élève reconnecté : un `away` d'un instant)
 * n'en font qu'une, comme les activités (`FOCUS_BLIP`) : relevé en recette, une barre coupée en deux Concentré.
 */
function mergeBlips(kept: Segment[]): Segment[] {
    const out: Segment[] = []
    for (const segment of kept) {
        const last = out[out.length - 1]
        if (last != null && last.value === segment.value && segment.from - last.to < FOCUS_BLIP) {
            out[out.length - 1] = { ...last, to: segment.to }
        } else out.push(segment)
    }
    return out
}

/**
 * Un Dialogue plus tôt que ceci avant l'heure officielle est un reste d'une autre semaine (la mémoire est rangée par
 * séance, pas par occurrence), pas un live en avance.
 */
const EARLY_START_AT_MOST = 30 * MINUTE

/**
 * Le début de la séance (P-5064) : l'heure officielle du live, ou le premier passage d'un élève en Dialogue s'il est
 * plus tôt (un live commencé en avance) — jamais la connexion du tuteur. Un élève absent n'a pas de mode
 * (`tutorLiveTimelineChanges` écrit `away`) : son Dialogue par défaut ne compte pas.
 */
export function tutorSessionStart(records: TutorLiveFeedRecord[], officialStart: number): number {
    let start = officialStart
    for (const record of records) {
        if (record.kind !== "mode" || record.value !== "dialogue") continue
        if (record.at < start && officialStart - record.at <= EARLY_START_AT_MOST) start = record.at
    }
    return start
}

/**
 * La barre de la séance d'un élève, du début du live (`tutorSessionStart`) à maintenant, en minutes : ses plages
 * Dialogue et Concentré, ses sorties de l'appli, et les événements (main levée, rendu, résumé du champion, demande du
 * tuteur).
 * `null` : rien encore à montrer.
 */
export function tutorSessionRibbon(input: {
    records: TutorLiveFeedRecord[]
    studentId: string
    /** L'heure officielle du live (ms). */
    officialStart: number
    now: number
    activities: UITutorLiveActivity[]
    clock: (ms: number) => string
    /** Les points qui ne vivent pas dans la mémoire, de cet élève. */
    extra?: TutorThreadExtra[]
    /** Le début de la séance (`tutorSessionStart`), déjà lu sur toute la mémoire : `records` peut alors être celle de l'élève seul. */
    startedAt?: number
    /** Ses entrées dans la salle que le serveur a notées (`joinedCallTimes`) : avant aussi l'arrivée de ce navigateur. */
    joins?: CallJoin[]
}): UITutorSessionRibbon | null {
    const { records, studentId, now } = input
    const startedAt = input.startedAt ?? tutorSessionStart(records, input.officialStart)
    const minute = (ms: number) => Math.max(0, (ms - startedAt) / MINUTE)
    // Avant le début, rien ne se dessine : un Dialogue d'avant commence au début de la barre.
    const clip = (segment: Segment) => ({ ...segment, from: Math.max(segment.from, startedAt) })
    const nowMinute = minute(now)
    const modes = mergeBlips(
        segments(records, "mode", studentId, now)
            .filter((segment) => segment.to > startedAt)
            .map(clip)
            .filter((segment) => segment.value === "dialogue" || segment.value === "focused")
    ).map((segment) => ({
        from: minute(segment.from),
        to: minute(segment.to),
        mode: segment.value as "dialogue" | "focused",
    }))
    const titles = new Map(input.activities.map((activity) => [activity.id, activity]))
    const activityOf = (id: string | undefined) => (id != null ? titles.get(id) : undefined)
    const raw: { id: string; at: number; kind: UITutorRibbonEventKind; label: string }[] = []
    for (const record of records) {
        if (record.studentId !== studentId || record.at < startedAt || record.at > now) continue
        const event = ribbonEvent(record, activityOf)
        if (event != null) raw.push({ id: record.id, at: record.at, ...event })
    }
    for (const extra of input.extra ?? []) if (extra.at >= startedAt && extra.at <= now) raw.push(extra)
    // Une entrée avant le début se lit au début ; deux entrées à moins d'une minute sont la même.
    for (const at of sessionJoins(input.joins ?? [], startedAt, now))
        raw.push({ id: `join-${studentId}-${at}`, at, kind: "join", label: TEXTS_RIBBON.join })
    raw.sort((a, b) => a.at - b.at)
    const events = raw.map((event) => ({
        id: event.id,
        at: minute(event.at),
        kind: event.kind,
        label: `${input.clock(event.at)} · ${event.label}`,
    }))
    const away = segments(records, "app", studentId, now)
        .filter((segment) => segment.value === "out" && segment.to > startedAt)
        .map(clip)
        .map((segment) => ({ from: minute(segment.from), to: minute(segment.to) }))
    if (nowMinute <= 0 || (modes.length === 0 && events.length === 0 && away.length === 0)) return null
    // La barre part de la première présence de l'élève (daily du 05/11, pas de vide à gauche) : un mode, une sortie, une
    // entrée. Un geste du masteur d'avant son arrivée (une bulle, une proposition) se pose au bord, sans tirer la barre.
    const from = Math.min(
        ...modes.map((range) => range.from),
        ...events.filter((event) => event.kind === "join").map((event) => event.at),
        ...away.map((range) => range.from)
    )
    return {
        now: nowMinute,
        startedAt: isoOf(startedAt),
        // Sans présence notée (un geste du masteur seul), `from` est l'infini : la barre part alors du début.
        ...(Number.isFinite(from) && from > 0 && { from }),
        modes,
        events,
        ...(away.length > 0 && { away }),
    }
}

const TEXTS_RIBBON = { join: "Connexion" }
const JOIN_SAME_WITHIN = MINUTE

/** Une entrée dans la salle notée par le serveur, et la sortie qui l'a suivie (absente : il n'en est pas ressorti). */
export type CallJoin = { at: number; leftAt?: number }

/**
 * Ses entrées dans la salle, à l'heure : seules celles que le serveur a notées. Un retour vu par ce navigateur (absent,
 * puis là) n'en est pas une : le masteur qui recharge sa salle voit chaque élève absent, puis là. Une entrée d'avant le
 * début se lit au début s'il est resté jusque-là ; deux entrées à moins d'une minute n'en font qu'une.
 */
function sessionJoins(joins: CallJoin[], startedAt: number, now: number): number[] {
    const kept: number[] = []
    for (const join of [...joins].sort((a, b) => a.at - b.at)) {
        if (join.at > now || (join.at < startedAt && join.leftAt != null && join.leftAt < startedAt)) continue
        const at = Math.max(join.at, startedAt)
        if (kept.length === 0 || at - kept[kept.length - 1] >= JOIN_SAME_WITHIN) kept.push(at)
    }
    return kept
}

/**
 * Les entrées dans la salle de chaque élève, lues sur les journaux du jour (`joined_call`, puis le `left_call` qui la
 * suit ; `created_at` à l'heure locale « AAAA-MM-JJ HH:mm:ss »). Un journal sans élève ou sans heure lisible ne compte
 * pas.
 */
export function joinedCallTimes(
    logs: readonly ({ action?: string | null; user_id?: string | number | null; created_at?: string | null } | null)[]
): Record<string, CallJoin[]> {
    const timed = logs
        .flatMap((log) => {
            if (log == null || log.user_id == null || log.created_at == null) return []
            if (log.action !== "joined_call" && log.action !== "left_call") return []
            const at = Date.parse(log.created_at.replace(" ", "T"))
            return Number.isNaN(at) ? [] : [{ id: String(log.user_id), joined: log.action === "joined_call", at }]
        })
        .sort((a, b) => a.at - b.at)
    const joins: Record<string, CallJoin[]> = {}
    for (const log of timed) {
        const mine = (joins[log.id] ??= [])
        const last = mine[mine.length - 1]
        if (log.joined) mine.push({ at: log.at })
        else if (last != null && last.leftAt == null) last.leftAt = log.at
    }
    for (const id of Object.keys(joins)) if (joins[id].length === 0) delete joins[id]
    return joins
}

/**
 * Le fil de chaque élève et le début de la séance, en une passe : le début lu une fois sur toute la mémoire, chaque
 * fil sur la mémoire de son élève seul (la mémoire d'un live grandit jusqu'à sa fin, et chaque tuile se relit à
 * chaque rendu de la salle).
 */
export function tutorSessionThreads(input: {
    records: TutorLiveFeedRecord[]
    students: { id: string; activities: UITutorLiveActivity[] }[]
    officialStart: number
    now: number
    clock: (ms: number) => string
    transient: TutorLiveTransientEntry[]
    /** Les entrées de chaque élève notées par le serveur (`joinedCallTimes`). */
    joins?: Record<string, CallJoin[]>
}): { startedAt: number; threads: Record<string, UITutorSessionRibbon | null> } {
    const startedAt = tutorSessionStart(input.records, input.officialStart)
    const mine = new Map<string, TutorLiveFeedRecord[]>()
    for (const record of input.records) {
        if (record.studentId == null) continue
        const list = mine.get(record.studentId)
        if (list != null) list.push(record)
        else mine.set(record.studentId, [record])
    }
    const extras = tutorThreadExtras(input.transient)
    const threads: Record<string, UITutorSessionRibbon | null> = {}
    for (const student of input.students)
        threads[student.id] = tutorSessionRibbon({
            records: mine.get(student.id) ?? [],
            studentId: student.id,
            officialStart: input.officialStart,
            startedAt,
            now: input.now,
            activities: student.activities,
            clock: input.clock,
            extra: extras[student.id],
            joins: input.joins?.[student.id],
        })
    return { startedAt, threads }
}

/** Le ton du score d'une activité faite : validée, refusée, sinon neutre. */
function scoreTone(activity: UITutorLiveActivity): "success" | "danger" | "neutral" {
    if (activity.reviewStatus === "validated") return "success"
    if (activity.reviewStatus === "rejected") return "danger"
    return "neutral"
}

/** La progression d'une activité : faite, pleine ; en cours, son temps passé sur le temps prévu ; sinon rien. */
function activityProgress(activity: UITutorLiveActivity): number | undefined {
    if (activity.status === "done") return 100
    if (activity.minutes == null || activity.estimatedMinutes == null || activity.estimatedMinutes <= 0)
        return undefined
    return Math.min(100, Math.round((activity.minutes / activity.estimatedMinutes) * 100))
}

/**
 * Les traces du fil rangées sous l'activité que l'élève avait devant lui à leur instant (Wajih, 04/11 : « l'historique
 * des actions de l'élève doit monter d'un niveau visuellement ») : une suite de traces d'une même activité devient un
 * groupe (son titre, sa progression, son score, son temps passé) ; tout le reste (le champion, le tuteur, une
 * génération, le repère du dernier passage) garde sa place et son heure. Une trace hors de toute activité connue reste
 * une trace.
 */
export function groupHistoryByActivity(
    history: UITutorSessionEntry[],
    focus: Segment[],
    activities: UITutorLiveActivity[]
): UITutorSessionEntry[] {
    const known = new Map(activities.map((activity) => [activity.id, activity]))
    const activityAt = (iso: string) => {
        const at = Date.parse(iso)
        return focus.find((segment) => segment.from <= at && at < segment.to)?.value ?? null
    }
    const out: UITutorSessionEntry[] = []
    for (const entry of history) {
        const activityId = entry.kind === "trace" ? activityAt(entry.at) : null
        const activity = activityId != null ? known.get(activityId) : undefined
        if (entry.kind !== "trace" || activity == null) {
            out.push(entry)
            continue
        }
        const last = out[out.length - 1]
        const trace = {
            id: entry.id,
            at: entry.at,
            text: entry.text,
            ...(entry.outcome != null && { outcome: entry.outcome }),
            ...(entry.quiet === true && { quiet: true }),
        }
        if (last?.kind === "activity" && last.id.startsWith(`activity-${activity.id}-`)) {
            out[out.length - 1] = { ...last, traces: [...last.traces, trace] }
            continue
        }
        out.push({
            kind: "activity",
            id: `activity-${activity.id}-${entry.id}`,
            at: entry.at,
            activityId: activity.id,
            title: activity.title,
            status: activity.status,
            progress: activityProgress(activity),
            score: activity.score != null ? { text: activity.score, tone: scoreTone(activity) } : undefined,
            time: activity.minutes != null ? `${activity.minutes} min` : undefined,
            traces: [trace],
        })
    }
    return out
}
